import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { Sidebar, SidebarContent } from './components/Sidebar'
import { Dashboard } from './pages/Dashboard'
import { CalendarPage } from './pages/Calendar'
import { ApprovalsPage } from './pages/Approvals'
import { WritersPage } from './pages/Writers'
import { AgentsPage } from './pages/Agents'
import { AgentChatPage } from './pages/AgentChat'
import { BriefsPage } from './pages/Briefs'
import { SocialPage } from './pages/Social'
import { AnalyticsPage } from './pages/Analytics'
import { InvoicingPage } from './pages/Invoicing'
import { RssPage } from './pages/Rss'

function MobileHeader({ onMenu }: { onMenu: () => void }) {
  return (
    <header className="md:hidden sticky top-0 z-30 h-14 bg-surface-card border-b border-border flex items-center gap-3 px-3">
      <button
        onClick={onMenu}
        aria-label="Open navigation"
        className="p-2 -ml-1 rounded-md hover:bg-surface-muted"
      >
        <Menu size={20} />
      </button>
      <img src="/logo.png" alt="Deadreckoning" className="h-6 w-auto object-contain" />
      <div className="text-sm font-medium">Deadreckoning</div>
      <div className="flex-1" />
      <div className="w-8 h-8 rounded-full bg-brand-50 text-brand-800 text-xs font-medium flex items-center justify-center">
        AM
      </div>
    </header>
  )
}

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (open) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        className={`md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-[280px] bg-surface-card border-r border-border flex flex-col transition-transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-2 px-3 py-4 border-b border-border">
          <img src="/logo.png" alt="Deadreckoning" className="h-6 w-auto object-contain" />
          <div className="text-sm font-medium leading-tight">Deadreckoning</div>
          <button
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto p-1 rounded-md hover:bg-surface-muted"
          >
            <X size={16} />
          </button>
        </div>
        <SidebarContent onNavigate={onClose} />
      </aside>
    </>
  )
}

export default function App() {
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div className="flex h-full">
      <Sidebar />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <MobileHeader onMenu={() => setDrawerOpen(true)} />
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/briefs" element={<BriefsPage />} />
            <Route path="/writers" element={<WritersPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/agents/:agentId/chat" element={<AgentChatPage />} />
            <Route path="/social" element={<SocialPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/invoicing" element={<InvoicingPage />} />
            <Route path="/rss" element={<RssPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
